<script lang="ts">
    // Folha de venda (v4, mobile). É SÓ a apresentação: toda regra — pacotes e seus itens filhos, rateio
    // do desconto, taxa por parcela, limite de desconto da permissão, caixa, importação da agenda,
    // conversão entre venda e orçamento — continua no pos-form.svelte, que no mobile entrega o estado
    // para cá em vez de desenhar o formulário antigo. Assim existe uma fonte de regra só.
    //
    // Os editores de item e de pagamento também são os do formulário atual (pos-entries-item/payment):
    // eles carregam regra (preço e comissão ao escolher o item; taxa do meio de pagamento ao confirmar).
    // Aqui desenhamos só as linhas fechadas, no padrão da folha, e abrimos o editor ao tocar.
    import { tick } from 'svelte'
    import Fa from 'svelte-fa'
    import { faBoxOpen, faCreditCard, faFileImport, faPlus, faPrint, faRepeat, faTrashAlt, faUser } from '@fortawesome/free-solid-svg-icons'
    import { slide } from 'svelte/transition'
    import type { Invoice, ServicesOnInvoices, PaymentMethodsOnInvoices } from '../../../modules/posApi/invoices/invoice'
    import type { Referral } from '../../../modules/eventsApi/referrals/referral'
    import Zsheet from '../../_common/z/sheet/zsheet.svelte'
    import ZsheetSection from '../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetEntity from '../../_common/z/sheet/zsheet-entity.svelte'
    import ZsheetOptions from '../../_common/z/sheet/zsheet-options.svelte'
    import type { ZsheetOption } from '../../_common/z/sheet/zsheet'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import ReferralSelect from '../../account/referrals/referral-select.svelte'
    import PosEntriesItem from '../pos-entries-item.svelte'
    import PosEntriesPayment from '../pos-entries-payment.svelte'

    // ---- estado (vem do pos-form, com bind nos que a folha altera) ----
    export let entity: Invoice
    export let createdAt: Date
    export let referral: Referral = null
    export let budget: boolean = false
    export let newService: boolean = false
    export let newPayment: boolean = false
    export let valuePending: number = 0
    export let totalGross: number = 0
    export let noPaid: number = 0
    export let show: boolean = false

    // ---- ações (funções do pos-form: a regra mora lá) ----
    export let onSave: () => void
    export let onRemove: () => void
    export let onPrint: () => void
    export let onConvertToInvoice: () => void
    export let onConvertToBudget: () => void
    export let onPersonChange: () => void
    export let onImport: () => void
    export let onServiceAdded: (ev: any, position: number) => void
    export let onServiceClosed: () => void
    export let onPaymentAdded: (ev: any) => void
    export let onPaymentClosed: () => void
    export let onDiscountChange: () => void

    const canWrite = !!window.store.token.permissions.pos.sales.write
    const money = (value: number) => window.api.utils.utils.toReal(value || 0)

    // ---- itens ----
    // índice do item aberto no editor (null = nenhum). Novo item usa a flag newService do pos-form.
    let editingItem: number = null

    function itemLabel(item: ServicesOnInvoices) {
        const type = item?.service?.type
        if (item?.parentServiceId) return ''
        if (type === 'package') return 'Pacote'
        if (type === 'product') return 'Produto'
        if (type === 'consumable') return 'Insumo'
        return 'Serviço'
    }

    function itemSubtitle(item: ServicesOnInvoices) {
        const parts = []
        if (!item.parentServiceId) parts.push(item.user?.userName || item.user?.email || '[sem vendedor]')
        if (item.service?.type !== 'package') parts.push(`${item.quantity || 0} × ${money(item.valueGross)}`)
        return parts.join(' · ')
    }

    function openItem(index: number) {
        newService = false
        editingItem = index
    }

    // o editor fecha por conta própria ao confirmar (✓) ou excluir (lixeira) — como no desktop
    function itemFinished() {
        editingItem = null
        onServiceClosed()
    }

    function startNewItem() {
        editingItem = null
        newService = true
    }

    // ---- pagamentos ----
    let editingPayment: number = null

    function paymentSubtitle(payment: PaymentMethodsOnInvoices) {
        const parts = [payment.installments > 1 ? `${payment.installments}x` : 'À vista']
        if (payment.firstDueInDays) parts.push(`1º vencimento em ${payment.firstDueInDays} dias`)
        return parts.join(' · ')
    }

    function openPayment(index: number) {
        newPayment = false
        editingPayment = index
    }

    function paymentFinished() {
        editingPayment = null
        onPaymentClosed()
    }

    function startNewPayment() {
        editingPayment = null
        newPayment = true
    }

    // ---- desconto ----
    // Mesmas regras do popover antigo (pos-form-discount.svelte): valor fixo OU percentual, nunca os dois,
    // e percentual entre 0 e 100. O total do rodapé acompanha enquanto se digita.
    let discountType: 'fixed' | 'percentage' = 'fixed'
    let discountValue: number = 0
    // a venda chega depois da folha montar (o pos-form carrega da API): quando ela troca, relê o desconto
    let discountSyncedFor: string = undefined
    $: syncDiscount(entity?.invoiceId || '')
    function syncDiscount(id: string) {
        if (id === discountSyncedFor) return
        discountSyncedFor = id
        discountType = entity?.discountPercentage > 0 ? 'percentage' : 'fixed'
        discountValue = (entity?.discountFixed || 0) + (entity?.discountPercentage || 0)
    }

    const discountOptions: ZsheetOption[] = [
        { value: 'fixed', label: 'Valor (R$)' },
        { value: 'percentage', label: 'Percentual (%)' },
    ]

    function applyDiscount() {
        const value = Number(discountValue) || 0
        if (value < 0) {
            window.error('Desconto inválido')
            return
        }
        if (discountType === 'percentage') {
            if (value > 100) {
                window.error('Desconto inválido')
                return
            }
            entity.discountFixed = 0
            entity.discountPercentage = value
        } else {
            entity.discountFixed = value
            entity.discountPercentage = 0
        }
        onDiscountChange()
    }

    // Como o bloco de sala/insumos da agenda: recolhido quando não há desconto, e aberto (sem poder
    // recolher) quando há — um desconto aplicado não pode ficar escondido. Depois que a pessoa mexe
    // no bloco, vale a escolha dela.
    $: hasDiscount = entity?.discountFixed > 0 || entity?.discountPercentage > 0
    let discountOpen = false
    let discountTouched = false
    $: if (!discountTouched) discountOpen = hasDiscount

    function onDiscountTypeChange(ev) {
        discountType = ev.detail
        applyDiscount()
    }

    // ---- resumo ----
    $: discountLabel =
        entity?.discountPercentage > 0 ? `${entity.discountPercentage}%` : entity?.discountFixed > 0 ? money(entity.discountFixed) : '—'
    $: isCredit = valuePending < 0
    $: services = (entity?.services || []).filter((el) => !!el)
    $: payments = (entity?.payments || []).filter((el) => !!el)
    $: summary = [
        `Total ${money(entity?.total)}`,
        !budget ? (isCredit ? `crédito ${money(-valuePending)}` : `em aberto ${money(valuePending)}`) : '',
    ]
        .filter(Boolean)
        .join(' · ')

    // ---- cliente ----
    $: person = entity?.person
    $: personInitials = (person?.name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((el) => el[0])
        .join('')

    async function clearPerson() {
        entity.person = null
        entity.personId = null
        await tick()
        onPersonChange()
    }
</script>

<Zsheet>
    <!-- CLIENTE -->
    <ZsheetSection label="Cliente" meta={person?.personId ? '' : 'obrigatório'}>
        <!-- personId, e não person: ao tocar em "+" para cadastrar, o seletor preenche a pessoa em branco
             (ainda sem id) e abre o cadastro dentro dele — testando só person, ele sairia da tela junto -->
        {#if person?.personId}
            <ZsheetEntity
                initials={personInitials}
                icon={faUser}
                title={person.name}
                subtitle={person.phone ? window.api.utils.utils.phoneFriendly(person.phone) : ''}
                removable={!entity.invoiceId}
                removeTitle="Trocar cliente"
                on:remove={clearPerson}
            />
            {#if noPaid > 0.1 || noPaid < -0.1}
                <div class="balance" class:positive={noPaid < -0.1}>
                    {noPaid > 0.1 ? `Cliente com valores em aberto: ${money(noPaid)}` : `Cliente com saldo: ${money(-noPaid)}`}
                </div>
            {/if}
        {:else}
            <ZentitySelect fonte="pessoa" label="Buscar por nome, telefone ou e-mail" bind:value={entity.person} on:change={onPersonChange} />
        {/if}
    </ZsheetSection>

    <!-- ITENS -->
    <ZsheetSection label={budget ? 'Itens do orçamento' : 'Itens'} meta={services.length ? `${services.length} ${services.length === 1 ? 'item' : 'itens'}` : ''}>
        <div class="list">
            {#each entity.services || [] as item, index}
                {#if item}
                <div transition:slide={{ duration: 160 }} class:child={!!item.parentServiceId}>
                    {#if editingItem === index}
                        <div class="edit">
                            <PosEntriesItem
                                startEditing={true}
                                bind:invoiceItem={entity.services[index]}
                                on:cancel={itemFinished}
                                on:added={itemFinished}
                            />
                        </div>
                    {:else}
                        <ZsheetEntity
                            icon={item.parentServiceId ? null : item.service?.type === 'package' ? faBoxOpen : null}
                            initials={item.parentServiceId || item.service?.type === 'package' ? '' : (item.service?.name || '?').substring(0, 1)}
                            title={item.service?.name || '[item não cadastrado]'}
                            subtitle={itemSubtitle(item)}
                            clickable={canWrite}
                            on:click={() => canWrite && openItem(index)}
                        >
                            {#if itemLabel(item)}<span class="tag">{itemLabel(item)}</span>{/if}
                            <svelte:fragment slot="trailing">
                                {#if item.service?.type !== 'package'}
                                    <span class="amount">{money((item.valueGross || 0) * (item.quantity || 0))}</span>
                                {/if}
                            </svelte:fragment>
                        </ZsheetEntity>
                    {/if}
                </div>
                {/if}
            {/each}

            {#if newService}
                <div class="edit" transition:slide={{ duration: 160 }}>
                    <PosEntriesItem on:cancel={onServiceClosed} on:added={(ev) => onServiceAdded(ev, entity.services.length - 1)} />
                </div>
            {:else if canWrite}
                <div class="adds">
                    <button type="button" class="add" on:click={startNewItem}><Fa icon={faPlus} /> Item</button>
                    <button type="button" class="add" on:click={onImport}><Fa icon={faFileImport} /> Da agenda</button>
                </div>
            {/if}
        </div>
    </ZsheetSection>

    <!-- DESCONTO -->
    {#if services.length > 0 && canWrite}
        <ZsheetSection
            label="Desconto"
            collapsible={!hasDiscount}
            bind:open={discountOpen}
            on:toggle={() => (discountTouched = true)}
            meta={!discountOpen && hasDiscount ? discountLabel : ''}
        >
            <div class="discount">
                <ZsheetOptions options={discountOptions} value={discountType} on:change={onDiscountTypeChange} class="discount_type" />
                <Zinput
                    type="number"
                    label={discountType === 'percentage' ? 'Percentual de desconto' : 'Valor do desconto'}
                    bind:value={discountValue}
                    on:change={applyDiscount}
                    on:blur={applyDiscount}
                />
            </div>
        </ZsheetSection>
    {/if}

    <!-- PAGAMENTO -->
    <ZsheetSection label={budget ? 'Alternativas de pagamento' : 'Pagamento'} meta={payments.length ? money(entity?.totalPaid) : ''}>
        <div class="list">
            {#each entity.payments || [] as payment, index}
                {#if payment}
                <div transition:slide={{ duration: 160 }}>
                    {#if editingPayment === index}
                        <div class="edit">
                            <PosEntriesPayment
                                startEditing={true}
                                pendingTotal={valuePending}
                                bind:invoicePayment={entity.payments[index]}
                                on:cancel={paymentFinished}
                                on:added={paymentFinished}
                            />
                        </div>
                    {:else}
                        <ZsheetEntity
                            icon={faCreditCard}
                            title={payment.paymentMethod?.label || '[meio de pagamento]'}
                            subtitle={paymentSubtitle(payment)}
                            clickable={canWrite}
                            on:click={() => canWrite && openPayment(index)}
                        >
                            <svelte:fragment slot="trailing">
                                <span class="amount">{money(payment.value)}</span>
                            </svelte:fragment>
                        </ZsheetEntity>
                    {/if}
                </div>
                {/if}
            {/each}

            {#if newPayment}
                <div class="edit" transition:slide={{ duration: 160 }}>
                    <PosEntriesPayment pendingTotal={valuePending} on:cancel={onPaymentClosed} on:added={onPaymentAdded} />
                </div>
            {:else if canWrite}
                <button type="button" class="add" on:click={startNewPayment}><Fa icon={faPlus} /> Pagamento</button>
            {/if}
        </div>
    </ZsheetSection>

    <!-- RESUMO -->
    {#if !budget}
        <ZsheetSection label="Resumo">
            <div class="totals">
                <div class="totals_row"><span>Itens</span><span>{money(totalGross)}</span></div>
                <div class="totals_row"><span>Desconto</span><span>{discountLabel}</span></div>
                <div class="totals_row -strong"><span>Total</span><span>{money(entity?.total)}</span></div>
                <div class="totals_row"><span>Pago</span><span>{money(entity?.totalPaid)}</span></div>
                <div class="totals_row -pending" class:-credit={isCredit} class:-open={!isCredit && valuePending > 0.004}>
                    <span>{isCredit ? 'Crédito ao cliente' : 'Em aberto'}</span>
                    <span>{money(isCredit ? -valuePending : valuePending)}</span>
                </div>
            </div>
        </ZsheetSection>
    {/if}

    <!-- DETALHES -->
    <ZsheetSection label="Detalhes">
        <div class="details">
            <Zinput type="datetime" label="Data do lançamento" bind:value={createdAt} />
            <ReferralSelect label="Origem" bind:referral />
            <Zinput type="textarea" rows={budget ? 4 : 2} label="Anotações" bind:value={entity.notes} />
        </div>
    </ZsheetSection>

    <!-- AÇÕES DE ORÇAMENTO -->
    {#if canWrite && entity.status === 'budget'}
        <ZsheetSection label="Orçamento">
            <div class="actions">
                <button type="button" class="add -solid" on:click={onPrint}><Fa icon={faPrint} /> Imprimir</button>
                {#if budget}
                    <button type="button" class="add -solid" on:click={onConvertToInvoice}><Fa icon={faRepeat} /> Converter em venda</button>
                {:else}
                    <button type="button" class="add -solid" on:click={onConvertToBudget}><Fa icon={faRepeat} /> Voltar a ser orçamento</button>
                {/if}
            </div>
        </ZsheetSection>
    {/if}

    <!-- EXCLUIR -->
    {#if canWrite && entity.invoiceId}
        <ZsheetSection tone="danger">
            <button type="button" class="danger" disabled={show} on:click={onRemove}>
                <span class="danger_icon"><Fa icon={faTrashAlt} /></span>
                <span class="danger_text">
                    <span class="danger_title">{budget ? 'Excluir orçamento' : 'Excluir venda'}</span>
                    <span class="danger_hint">Apaga este lançamento.</span>
                </span>
            </button>
        </ZsheetSection>
    {/if}

    <svelte:fragment slot="footer">
        <div class="footer">
            <div class="footer_summary" class:-open={!budget && !isCredit && valuePending > 0.004}>{summary}</div>
            {#if canWrite}
                <button type="button" class="footer_save" disabled={show} on:click={onSave}>
                    {show ? 'Salvando…' : budget ? 'Salvar orçamento' : entity.invoiceId ? 'Salvar alterações' : 'Salvar venda'}
                </button>
            {/if}
        </div>
    </svelte:fragment>
</Zsheet>

<style>
    /* valores base = tema escuro (padrão do app) */
    .list {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    /* item de pacote: recuado, embaixo do pacote a que pertence */
    .child {
        margin-left: 22px;
        opacity: 0.85;
    }
    .edit {
        padding: 10px;
        border-radius: 10px;
        background: var(--z-field-bg);
        border: 1px solid #2ecc7144;
    }
    .tag {
        display: inline-block;
        font-size: 11px;
        font-weight: 600;
        padding: 1px 7px;
        border-radius: 999px;
        background: var(--z-field-act-bg);
        margin-top: 3px;
    }
    .amount {
        flex: 0 0 auto;
        font-size: 14.5px;
        font-weight: 700;
        white-space: nowrap;
    }
    .adds {
        display: flex;
        gap: 6px;
    }
    .adds .add {
        flex: 1 1 0;
    }
    .add {
        border: 1px dashed #ffffff33;
        background: none;
        color: inherit;
        font: inherit;
        font-size: 14px;
        border-radius: 10px;
        padding: 10px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: var(--z-field-height);
    }
    .add.-solid {
        border-style: solid;
        border-color: var(--z-field-border);
        background: var(--z-field-bg);
    }

    .discount {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .discount :global(.discount_type) {
        flex-direction: row;
    }
    .discount :global(.discount_type .zoption) {
        flex: 1 1 0;
        justify-content: center;
    }

    .totals {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .totals_row {
        display: flex;
        justify-content: space-between;
        font-size: 14px;
    }
    .totals_row > span:first-child {
        opacity: 0.6;
    }
    .totals_row.-strong {
        font-weight: 700;
        font-size: 15.5px;
        padding-top: 6px;
        border-top: 1px solid var(--z-field-border);
    }
    .totals_row.-pending {
        font-weight: 700;
        padding: 8px 10px;
        border-radius: 10px;
        background: var(--z-field-bg);
    }
    .totals_row.-pending > span:first-child {
        opacity: 1;
    }
    .totals_row.-open {
        background: #e0a83a1f;
        color: #e8bd6a;
    }
    .totals_row.-credit {
        background: #2ecc711f;
        color: #7fd8a4;
    }

    .details,
    .actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .balance {
        margin-top: 8px;
        font-size: 13px;
        padding: 8px 10px;
        border-radius: 10px;
        background: #e0a83a1f;
        color: #e8bd6a;
    }
    .balance.positive {
        background: #2ecc711f;
        color: #7fd8a4;
    }

    .danger {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        width: 100%;
        text-align: left;
        background: none;
        border: 0px;
        color: #e58080;
        font: inherit;
        padding: 0px;
    }
    .danger_icon {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        height: 19px;
        font-size: 14px;
    }
    .danger_text {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }
    .danger_title {
        font-size: 14.5px;
        font-weight: 600;
        line-height: 19px;
    }
    .danger_hint {
        font-size: 12.5px;
        opacity: 0.55;
        line-height: 1.3;
        margin-top: 3px;
    }

    .footer {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .footer_summary {
        font-size: 12.5px;
        opacity: 0.7;
        text-align: center;
    }
    .footer_summary.-open {
        color: #e8bd6a;
        opacity: 1;
    }
    .footer_save {
        width: 100%;
        border: 0px;
        border-radius: 12px;
        background: #2ecc71;
        color: #10331f;
        font: inherit;
        font-size: 16px;
        font-weight: 700;
        padding: 14px;
    }
    .footer_save:disabled {
        opacity: 0.4;
    }

    :global(.theme_light) .add {
        border-color: #d8dbe0;
    }
    :global(.theme_light) .totals_row.-open,
    :global(.theme_light) .balance {
        background: #fff4e0;
        color: #9a6b12;
    }
    :global(.theme_light) .totals_row.-credit,
    :global(.theme_light) .balance.positive {
        background: #e9f8ef;
        color: #1c7a45;
    }
    :global(.theme_light) .footer_summary.-open {
        color: #9a6b12;
    }
    :global(.theme_light) .footer_save {
        color: #ffffff;
    }
    :global(.theme_light) .danger {
        color: #c0392b;
    }
</style>
